import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { LoginScreen } from '../../features/auth/LoginScreen';
import { RegisterScreen } from '../../features/auth/RegisterScreen';
import { VerifyEmailScreen } from '../../features/auth/VerifyEmailScreen';
import { OfflineUnverifiableScreen } from '../../features/auth/OfflineUnverifiableScreen';
import { ResetFailedScreen } from '../../features/auth/ResetFailedScreen';
import { ForcedPasswordSetupScreen } from './ForcedPasswordSetupScreen';
import { ROUTES } from '../../ui/navigation/routes';
import styles from './AuthGate.module.css';
import bannerStyles from './OfflineBanner.module.css';

/**
 * Renders `children` only once authentication is established.
 *
 * This is the entire enforcement mechanism: nothing under `children` — every
 * route, every screen — mounts until `isAuthenticated` is true. There is no
 * per-route guard to keep in sync, because there is only one gate, at the top.
 */
export function AuthGate({ children }: { children: ReactNode }): React.JSX.Element {
  const { status, isAuthenticated, isOffline, mustChangePassword } = useAuth();
  const location = useLocation();

  // Checked FIRST, ahead of every other branch including `checking` —
  // email-verification addendum. A verification link is redeemed purely from
  // the token in its own URL (§16: server-derived identity only), so it must
  // not be gated behind, delayed by, or hijacked by this device's
  // authentication state: `offline-unverifiable` would otherwise replace the
  // link's own screen on a flaky connection, and waiting for `/api/auth/me`
  // to resolve would gate a flow that has nothing to do with any session.
  // Whoever clicked the link is verifying whichever account the SERVER says
  // that token belongs to — never whichever account this browser happens to
  // have local state for.
  if (location.pathname === ROUTES.verifyEmail) {
    return <VerifyEmailScreen />;
  }

  if (status === 'checking') {
    // A network round-trip to /api/auth/me now, not just a local read — this
    // can genuinely take a moment on a slow connection. Visually blank
    // (just the correctly themed background, set synchronously by the
    // pre-paint script in index.html) rather than a small logo on an
    // otherwise empty page: a logo that appears only to vanish again a
    // moment later read as a broken load, not a splash. `role="status"` and
    // the visually-hidden text keep this announced to assistive tech even
    // though sighted users see nothing happen here.
    return (
      <div className={styles.loading} role="status" aria-busy="true">
        <span className={styles.visuallyHidden}>Loading LiftX…</span>
      </div>
    );
  }

  // Unreachable server and nothing recent enough to continue on. Distinct from
  // the login screen on purpose — there is no offline login (§11).
  if (status === 'offline-unverifiable') {
    return <OfflineUnverifiableScreen />;
  }

  // Phase G fix design: the server confirmed the session, but this device
  // could not be safely prepared for it (see `AuthStatus`'s own doc for
  // `'reset-failed'`). Distinct from the login screen — the credentials
  // were already correct — and distinct from `offline-unverifiable` — this
  // has nothing to do with connectivity.
  if (status === 'reset-failed') {
    return <ResetFailedScreen />;
  }

  if (!isAuthenticated) {
    // The one route-aware branch in an otherwise route-blind gate —
    // registration must be reachable without a session (Registration +
    // Approval plan §11). An already-authenticated visit to /register never
    // reaches this branch at all; it falls through to `children`, whose own
    // routes have no /register entry, so AppRoutes' catch-all redirects it
    // to the Dashboard — the existing "unknown route" convention, reused
    // rather than a bespoke redirect built for this one case.
    if (location.pathname === ROUTES.register) {
      return <RegisterScreen />;
    }
    return <LoginScreen />;
  }

  // Master Phase 11 §6/§8: authenticated via the provisional bootstrap
  // credential, but the one-time permanent-password transition hasn't
  // completed yet. Blocks the normal app — including CatalogueProvider's
  // seeding and every consumer screen — exactly like offline-unverifiable
  // does, and only ever fires from a fresh, online /me response (§8): this
  // state is deliberately never cached, so it can't survive offline.
  if (mustChangePassword) {
    return <ForcedPasswordSetupScreen />;
  }

  return (
    <>
      {/* §11: an offline session is continued, but never silently — the user
          should know their sign-in is running on cached verification. */}
      {isOffline && (
        <div className={bannerStyles.banner} role="status">
          Offline — signed in from cached verification
        </div>
      )}
      {children}
    </>
  );
}
